import { useEffect, useState } from 'react';
import {
  getAddressBlockExplorerUrl,
  getNetworkDisplayName,
  networkTypeSchema,
  type NetworkId,
  type NetworkType,
  type SimulationAsset,
  type SimulationParty,
  type AccountTransactionDraft,
} from '@mls-wallet/shared-types';
import {
  AlertCircle,
  ArrowDownRight,
  ArrowLeftRight,
  ArrowUpRight,
  ExternalLink,
  FileJson,
  RotateCw,
  X,
} from 'lucide-react';
import loader from '@/assets/transaction-simulation/loader.svg';
import { CopyAddressButton } from '@/components/shared/copy-address-button';
import { NetworkIcon } from '@/components/shared/network-icon';
import { TokenLogo } from '@/components/shared/token-logo';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  simulateStoredTransaction,
  simulateTransaction,
} from '@/lib/api/simulateTransaction';
import { toTransactionSimulationRequestFailure } from '@/lib/api/transactionSimulationRequestFailure';
import { cn, formatAddress } from '@/lib/utils';
import { toTransactionSimulationView } from './simulationViewModel';
import type {
  SimulationAllowance,
  SimulationBalanceChange,
  SimulationTokenActivity,
  SimulationTokenApproval,
  SuccessfulTransactionSimulationState,
  TransactionSimulationState,
} from './types';

type TransactionSimulationSheetBaseProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
};

type SimulationNetworkId = NetworkId | NetworkType;

type TransactionSimulationSheetProps = TransactionSimulationSheetBaseProps &
  (
    | {
        source: 'draft';
        request: AccountTransactionDraft;
        transactionId?: never;
      }
    | {
        source: 'stored';
        transactionId: string;
        networkId: SimulationNetworkId;
        request?: never;
      }
  );

/**
 * Renders resolved parties as address-book labels and unresolved parties as
 * truncated addresses. Every party keeps its copy and explorer actions, and
 * origins may also show the network on which the transaction is simulated.
 */
function PartyPill({
  party,
  networkId,
  showTransactionNetwork = false,
}: {
  party: SimulationParty;
  networkId: NetworkType;
  showTransactionNetwork?: boolean;
}): React.ReactElement {
  const label = party.label?.trim();
  const explorerUrl = getAddressBlockExplorerUrl(party.address, networkId);
  const networkName = getNetworkDisplayName(networkId);

  return (
    <span
      className={cn(
        'inline-flex h-[33px] min-w-0 max-w-full items-center justify-center overflow-hidden rounded-[43px] border border-zinc-200 text-[16px] leading-[normal] dark:border-zinc-700',
        label ? 'gap-2 pl-[14px] pr-0.5' : 'shrink-0 gap-0.5 pl-[14px] pr-0.5'
      )}
    >
      {showTransactionNetwork && (
        <span
          aria-label={`Network: ${networkName}`}
          title={networkName}
          className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-zinc-600 dark:text-zinc-400"
        >
          <NetworkIcon networkId={networkId} size="xs" />
          <span>{networkName}</span>
        </span>
      )}
      {label ? (
        <span
          title={label}
          className="min-w-0 truncate font-semibold text-black dark:text-white"
        >
          {label}
        </span>
      ) : (
        <span className="shrink-0 font-mono font-normal text-zinc-700 dark:text-zinc-300">
          {formatAddress(party.address)}
        </span>
      )}
      <span className="flex shrink-0 items-center">
        <CopyAddressButton
          address={party.address}
          ariaLabel={`Copy address ${party.address}`}
          className="h-7 w-7 text-zinc-600 dark:text-zinc-400"
        />
        {explorerUrl && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            asChild
            className="h-7 w-7 rounded-md p-0 text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
          >
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View address ${party.address} on block explorer`}
              title="View on block explorer"
            >
              <ExternalLink aria-hidden="true" className="size-3.5" />
            </a>
          </Button>
        )}
      </span>
    </span>
  );
}

function AssetPill({
  asset,
  amount,
}: {
  asset: SimulationAsset;
  amount?: string;
}): React.ReactElement {
  return (
    <span className="inline-flex h-[33px] shrink-0 items-center justify-center gap-[10px] overflow-hidden rounded-[72px] border border-zinc-200 bg-zinc-100 px-[10px] text-[16px] font-medium leading-[normal] text-black dark:border-zinc-700 dark:bg-zinc-800 dark:text-white">
      {amount && <span>{amount}</span>}
      <span className="inline-flex items-center gap-[3px]">
        <span aria-hidden="true">
          <TokenLogo symbol={asset.symbol} logoUrl={asset.logoUrl} size="sm" />
        </span>
        <span>{asset.symbol}</span>
      </span>
    </span>
  );
}

/** Renders an approval amount and its token identity as one non-wrapping label. */
function ApprovalAmount({
  asset,
  amount,
}: {
  asset: SimulationAsset;
  amount: string;
}): React.ReactElement {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[16px] text-black dark:text-white">
      <span className="font-semibold">{amount}</span>
      <span className="inline-flex items-center gap-1 font-medium">
        <span aria-hidden="true">
          <TokenLogo symbol={asset.symbol} logoUrl={asset.logoUrl} size="sm" />
        </span>
        <span>{asset.symbol}</span>
      </span>
    </span>
  );
}

/** Returns the unlimited label or the already-formatted finite allowance. */
function allowanceDisplay(allowance: SimulationAllowance): string {
  return allowance.kind === 'unlimited' ? 'Unlimited' : allowance.amount;
}

function SectionCard({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <section
      className={cn(
        'w-full overflow-hidden rounded-[14px] bg-zinc-100 shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)] ring-1 ring-inset ring-white dark:bg-zinc-900 dark:ring-zinc-800',
        className
      )}
    >
      <div className="flex h-[29px] items-center justify-center px-5 py-[7px]">
        <h3 className="text-center text-[12px] font-normal leading-[normal] text-gray-500 dark:text-zinc-400">
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

/**
 * Groups only the integer portion of an unsigned display amount and preserves
 * its fractional digits exactly (for example, `1234.5600` becomes
 * `1,234.5600`). BalanceChangeRow adds the direction sign separately.
 */
function groupDecimalAmount(amount: string): string {
  const [whole = '', fraction] = amount.split('.');
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return fraction === undefined ? groupedWhole : `${groupedWhole}.${fraction}`;
}

function BalanceChangeRow({
  change,
}: {
  change: SimulationBalanceChange;
}): React.ReactElement {
  const isIncrease = change.direction === 'increased';
  const signedAmount = `${isIncrease ? '+' : '-'}${groupDecimalAmount(
    change.amount
  )}`;

  return (
    <div className="grid min-h-[73px] grid-cols-3 items-center gap-x-6 border-b border-white bg-zinc-50 px-5 py-4 last:border-b-0 dark:border-zinc-800 dark:bg-zinc-950 max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:gap-x-3 max-sm:gap-y-1">
      <div className="inline-flex min-w-0 items-center gap-2 text-[16px] font-medium leading-[normal] text-black dark:text-white">
        <span aria-hidden="true" className="shrink-0">
          <TokenLogo
            symbol={change.asset.symbol}
            logoUrl={change.asset.logoUrl}
            size="sm"
          />
        </span>
        <span className="min-w-0 truncate" title={change.asset.symbol}>
          {change.asset.symbol}
        </span>
      </div>
      <div
        className={cn(
          'inline-flex shrink-0 items-center justify-self-center gap-1 whitespace-nowrap text-[16px] font-medium leading-[normal] max-sm:col-start-1 max-sm:row-start-2 max-sm:justify-self-start',
          isIncrease
            ? 'text-emerald-950 dark:text-emerald-300'
            : 'text-rose-900 dark:text-rose-300'
        )}
      >
        {isIncrease ? (
          <ArrowUpRight aria-hidden="true" className="size-6" />
        ) : (
          <ArrowDownRight aria-hidden="true" className="size-6" />
        )}
        <span>{change.direction}</span>
      </div>
      <div
        className={cn(
          'justify-self-end whitespace-nowrap text-right text-[16px] font-semibold leading-[normal] tabular-nums max-sm:col-start-2 max-sm:row-span-2 max-sm:row-start-1',
          isIncrease
            ? 'text-emerald-950 dark:text-emerald-300'
            : 'text-rose-900 dark:text-rose-300'
        )}
      >
        {signedAmount}
      </div>
    </div>
  );
}

function TokenApprovalRow({
  approval,
  networkId,
}: {
  approval: SimulationTokenApproval;
  networkId: NetworkType;
}): React.ReactElement {
  const allowance = allowanceDisplay(approval.allowance);

  return (
    <div className="flex min-h-[73px] flex-nowrap items-center gap-[9px] overflow-x-auto border-b border-white bg-zinc-50 p-5 text-[16px] leading-[normal] text-zinc-800 last:border-b-0 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-200">
      <span className="inline-flex shrink-0 items-center gap-[9px] whitespace-nowrap">
        <span>Approve</span>
        <ApprovalAmount asset={approval.asset} amount={allowance} />
      </span>
      <span className="inline-flex min-w-0 max-w-full items-center gap-[9px] whitespace-nowrap">
        <span className="shrink-0">to be spent by</span>
        <PartyPill party={approval.spender} networkId={networkId} />
      </span>
    </div>
  );
}

function EmptySection({ message }: { message: string }): React.ReactElement {
  return (
    <div className="bg-zinc-50 px-5 py-8 text-center text-sm text-zinc-500 dark:bg-zinc-950 dark:text-zinc-400">
      {message}
    </div>
  );
}

function SummaryTab({
  simulation,
  networkId,
}: {
  simulation: SuccessfulTransactionSimulationState;
  networkId: NetworkType;
}): React.ReactElement {
  return (
    <div className="mx-auto flex w-full max-w-[800px] flex-col gap-[30px]">
      <SectionCard title="Your Balance Changes">
        {simulation.balanceChanges.length === 0 ? (
          <EmptySection message="No balance changes" />
        ) : (
          simulation.balanceChanges.map((change, index) => (
            <BalanceChangeRow
              key={`${change.asset.address ?? 'native'}-${change.direction}-${index}`}
              change={change}
            />
          ))
        )}
      </SectionCard>
      <SectionCard title="New Token Approvals">
        {simulation.tokenApprovals.length === 0 ? (
          <EmptySection message="No new token approvals" />
        ) : (
          simulation.tokenApprovals.map((approval, index) => (
            <TokenApprovalRow
              key={`${approval.asset.address}-${approval.spender.address}-${index}`}
              approval={approval}
              networkId={networkId}
            />
          ))
        )}
      </SectionCard>
    </div>
  );
}

function TokenActivityRow({
  activity,
  networkId,
}: {
  activity: SimulationTokenActivity;
  networkId: NetworkType;
}): React.ReactElement {
  // Case: Native-token activity comes from account balance changes, not logs.
  if (activity.kind === 'native-balance-change') {
    return (
      <div className="flex h-[73px] items-center gap-[9px] border-b border-white bg-zinc-50 p-5 last:border-b-0 dark:border-zinc-800 dark:bg-zinc-950 max-lg:h-auto max-lg:min-h-[73px] max-lg:flex-wrap">
        <span className="inline-flex shrink-0 items-center gap-[9px] whitespace-nowrap text-[16px] leading-[normal] text-zinc-800 dark:text-zinc-200">
          <span>Balance {activity.direction} by</span>
          <AssetPill asset={activity.asset} amount={activity.amount} />
        </span>
        <span className="inline-flex min-w-0 max-w-full items-center gap-[9px] whitespace-nowrap text-[16px] leading-[normal] text-zinc-800 dark:text-zinc-200">
          <span className="shrink-0">for</span>
          <PartyPill party={activity.account} networkId={networkId} />
        </span>
      </div>
    );
  }

  // Case: Transfer activities have distinct origin and destination parties.
  if (activity.kind === 'transfer') {
    return (
      <div className="flex h-[73px] items-center gap-[9px] border-b border-white bg-zinc-50 p-5 last:border-b-0 dark:border-zinc-800 dark:bg-zinc-950 max-lg:h-auto max-lg:min-h-[73px] max-lg:flex-wrap">
        <span className="text-[16px] leading-[normal] text-zinc-800 dark:text-zinc-200">
          Send
        </span>
        <AssetPill asset={activity.asset} amount={activity.amount} />
        <span className="text-[16px] leading-[normal] text-zinc-800 dark:text-zinc-200">
          from
        </span>
        <PartyPill
          party={activity.from}
          networkId={networkId}
          showTransactionNetwork
        />
        <span className="text-[16px] leading-[normal] text-zinc-800 dark:text-zinc-200">
          to
        </span>
        <PartyPill party={activity.to} networkId={networkId} />
      </div>
    );
  }

  // Case: The remaining activity is an ERC-20 approval with owner and spender.
  const allowance = allowanceDisplay(activity.allowance);

  return (
    <div className="flex min-h-[73px] flex-nowrap items-center gap-[9px] overflow-x-auto border-b border-white bg-zinc-50 p-5 text-[16px] leading-[normal] text-zinc-800 last:border-b-0 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-200">
      <span className="inline-flex shrink-0 items-center gap-[9px] whitespace-nowrap">
        <span>Approve</span>
        <ApprovalAmount asset={activity.asset} amount={allowance} />
      </span>
      <span className="inline-flex min-w-0 max-w-full items-center gap-[9px] whitespace-nowrap">
        <span className="shrink-0">to be spent by</span>
        <PartyPill party={activity.spender} networkId={networkId} />
      </span>
      <span className="inline-flex min-w-0 max-w-full items-center gap-[9px] whitespace-nowrap">
        <span className="shrink-0">on behalf of</span>
        <PartyPill party={activity.owner} networkId={networkId} />
      </span>
    </div>
  );
}

function TokenTransfersTab({
  activities,
  networkId,
}: {
  activities: SimulationTokenActivity[];
  networkId: NetworkType;
}): React.ReactElement {
  return (
    <SectionCard title="Token Transfers" className="mx-auto max-w-[1043px]">
      {activities.length === 0 ? (
        <EmptySection message="No token transfers" />
      ) : (
        activities.map((activity, index) => (
          <TokenActivityRow
            key={index}
            activity={activity}
            networkId={networkId}
          />
        ))
      )}
    </SectionCard>
  );
}

function SimulationMetadata({
  simulation,
}: {
  simulation: SuccessfulTransactionSimulationState;
}): React.ReactElement {
  const hasGasUsed = simulation.metadata.gas.used !== undefined;

  return (
    <div className="flex min-h-[25px] flex-wrap items-center justify-center gap-[2px] text-[12px] leading-[normal] text-zinc-700 dark:text-zinc-300">
      {hasGasUsed && (
        <span className="px-[10px] py-[5px]">
          Gas Used:{' '}
          <span className="font-medium">{simulation.metadata.gas.used}</span>
        </span>
      )}
    </div>
  );
}

function SuccessContent({
  simulation,
  networkId,
}: {
  simulation: SuccessfulTransactionSimulationState;
  networkId: NetworkType;
}): React.ReactElement {
  return (
    <main className="min-h-0 flex-1 overflow-y-auto bg-zinc-100 p-[30px] dark:bg-zinc-900">
      <Tabs defaultValue="summary" className="w-full">
        <div className="mx-auto flex h-[97px] w-full max-w-[800px] flex-col items-center gap-[30px]">
          <SimulationMetadata simulation={simulation} />
          <TabsList className="h-[42px] gap-[5px] rounded-[36px] border border-white bg-zinc-100 p-[5px] dark:border-zinc-800 dark:bg-zinc-900">
            <TabsTrigger
              value="summary"
              className="gap-[10px] rounded-[31px] bg-zinc-100 px-3 py-[6px] text-[14px] font-medium leading-5 text-zinc-600 shadow-none data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-none dark:bg-zinc-900 dark:text-zinc-400 dark:data-[state=active]:bg-zinc-950 dark:data-[state=active]:text-white"
            >
              <ArrowLeftRight aria-hidden="true" className="size-[18px]" />
              Summary
            </TabsTrigger>
            <TabsTrigger
              value="token-transfers"
              className="gap-[10px] rounded-[31px] bg-zinc-100 px-3 py-[6px] text-[14px] font-medium leading-5 text-zinc-600 shadow-none data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-none dark:bg-zinc-900 dark:text-zinc-400 dark:data-[state=active]:bg-zinc-950 dark:data-[state=active]:text-white"
            >
              <FileJson aria-hidden="true" className="size-[18px]" />
              Token Transfers
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="summary" className="mt-[30px]">
          <SummaryTab simulation={simulation} networkId={networkId} />
        </TabsContent>
        <TabsContent value="token-transfers" className="mt-[30px]">
          <TokenTransfersTab
            activities={simulation.tokenActivities}
            networkId={networkId}
          />
        </TabsContent>
      </Tabs>
    </main>
  );
}

function LoadingContent(): React.ReactElement {
  return (
    <main
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-[30px] bg-zinc-100 p-[30px] dark:bg-zinc-900"
    >
      <div className="flex size-[126px] items-center justify-center">
        <img
          src={loader}
          alt=""
          className="size-[101px] animate-spin dark:brightness-200"
          aria-hidden="true"
        />
      </div>
      <p className="text-[20px] font-normal leading-[normal] text-slate-500 dark:text-slate-400">
        Simulating transaction...
      </p>
    </main>
  );
}

function RevertedContent({ reason }: { reason: string }): React.ReactElement {
  return (
    <main className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto bg-rose-50 p-[30px] text-rose-900 dark:bg-rose-950/30 dark:text-rose-300">
      <div className="flex w-full max-w-[636px] items-start overflow-hidden rounded-xl p-[30px]">
        <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-[15px]">
          <div className="flex items-center justify-center gap-[10px]">
            <AlertCircle aria-hidden="true" className="size-[27px] shrink-0" />
            <h2 className="text-[20px] font-semibold leading-[normal]">
              Transaction reverted during simulation
            </h2>
          </div>
          <p className="w-full text-center text-[16px] font-normal leading-[25px]">
            Double check that your transaction is configured correctly.
          </p>
          <div className="flex min-h-[88px] w-full flex-col justify-center gap-[10px] rounded-xl border border-rose-900 p-5 text-[16px] leading-[normal] dark:border-rose-700">
            <p className="font-bold">Revert Reason</p>
            <p className="break-words font-medium [overflow-wrap:anywhere]">
              {reason}
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

function ProviderFailureContent({
  message,
}: {
  message: string;
}): React.ReactElement {
  return (
    <main className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto bg-orange-50 p-[30px] text-orange-900 dark:bg-orange-950/30 dark:text-orange-300">
      <div className="flex w-full max-w-[636px] items-start overflow-hidden rounded-xl p-[30px]">
        <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-[15px]">
          <div className="flex items-center justify-center gap-[10px]">
            <AlertCircle aria-hidden="true" className="size-[27px] shrink-0" />
            <h2 className="text-[20px] font-semibold leading-[normal]">
              Unable to Simulate Transaction
            </h2>
          </div>
          <p className="w-full break-words text-center text-[16px] font-normal leading-[25px] [overflow-wrap:anywhere]">
            {message}
          </p>
        </div>
      </div>
    </main>
  );
}

function SimulationFooter({
  showResimulate,
  onResimulate,
}: {
  showResimulate: boolean;
  onResimulate: () => void;
}): React.ReactElement {
  const buttonClassName =
    'h-[37px] rounded-[9px] px-[19px] py-[6px] text-[14px] font-semibold text-zinc-900 dark:text-zinc-100';

  return (
    <footer className="flex h-[74px] shrink-0 items-center justify-center gap-[10px] border-t border-zinc-200 bg-white p-[10px] dark:border-zinc-800 dark:bg-zinc-950">
      {showResimulate && (
        <Button
          type="button"
          variant="outline"
          className={buttonClassName}
          onClick={onResimulate}
        >
          <RotateCw aria-hidden="true" className="size-[15px]" />
          Re-simulate
        </Button>
      )}
      <SheetClose asChild>
        <Button type="button" variant="outline" className={buttonClassName}>
          Close
        </Button>
      </SheetClose>
    </footer>
  );
}

/**
 * Owns the simulation request lifecycle for the sheet. Opening the sheet or
 * selecting Re-simulate starts a fresh request, while closing resets the view
 * and cleanup aborts any request superseded by a close, retry, or source change.
 */
export function TransactionSimulationSheet(
  props: TransactionSimulationSheetProps
): React.ReactElement {
  const { open, onOpenChange, returnFocusRef } = props;
  const draftRequest = props.source === 'draft' ? props.request : null;
  const storedTransactionId =
    props.source === 'stored' ? props.transactionId : null;
  const networkId = networkTypeSchema.parse(
    props.source === 'draft' ? props.request.networkId : props.networkId
  );
  const [simulationRun, setSimulationRun] = useState(0);
  const [simulation, setSimulation] = useState<TransactionSimulationState>({
    status: 'loading',
  });

  useEffect(() => {
    // Case: A closed sheet has no active result and returns to its loading state.
    if (!open) {
      setSimulation({ status: 'loading' });
      return;
    }

    // Each opening or retry owns only this request. Cleanup cancels its network
    // work, while the ownership guard also covers responses that race abort.
    let ownsRequest = true;
    const abortController = new AbortController();
    setSimulation({ status: 'loading' });

    let simulationRequest: ReturnType<typeof simulateTransaction>;
    if (draftRequest) {
      // Case: Draft sources simulate their currently encoded request.
      simulationRequest = simulateTransaction(
        draftRequest,
        abortController.signal
      );
    } else if (storedTransactionId) {
      // Case: Stored sources re-simulate the persisted transaction by ID.
      simulationRequest = simulateStoredTransaction(
        storedTransactionId,
        abortController.signal
      );
    } else {
      return;
    }

    void simulationRequest.then(
      (response) => {
        if (!ownsRequest) return;

        try {
          setSimulation(toTransactionSimulationView(response));
        } catch {
          // Case: A schema-invalid 2xx response is a simulation failure, not
          // a transport outage, and users may retry it like other failures.
          setSimulation({
            status: 'provider-failure',
            message: 'Please report this to the Den team.',
            retryable: true,
          });
        }
      },
      (error: unknown) => {
        if (ownsRequest) {
          const failure = toTransactionSimulationRequestFailure(error);
          setSimulation({
            status: 'provider-failure',
            ...failure,
          });
        }
      }
    );

    return () => {
      ownsRequest = false;
      abortController.abort();
    };
  }, [draftRequest, open, simulationRun, storedTransactionId]);

  const resimulate = () => {
    setSimulation({ status: 'loading' });
    setSimulationRun((run) => run + 1);
  };
  const showResimulate =
    simulation.status !== 'loading' &&
    (simulation.status !== 'provider-failure' || simulation.retryable);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        onCloseAutoFocus={(event) => {
          if (returnFocusRef?.current) {
            // This controlled sheet has no SheetTrigger, so explicitly return
            // focus to the action that opened it after Radix removes the modal.
            event.preventDefault();
            returnFocusRef.current.focus();
          }
        }}
        overlayClassName="bg-black/25 dark:bg-black/60"
        className="top-[35px] flex h-[calc(100dvh-35px)] w-full max-w-none flex-col gap-0 overflow-hidden rounded-t-[50px] border border-zinc-100 bg-white p-0 font-['Inter',sans-serif] dark:border-zinc-800 dark:bg-zinc-950"
      >
        <header className="relative flex h-[65px] shrink-0 items-center justify-center border-b border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
          <SheetTitle className="text-[20px] font-medium leading-5 text-black dark:text-white">
            Transaction Simulation
          </SheetTitle>
          <SheetDescription className="sr-only">
            Preview the simulated effects of this transaction.
          </SheetDescription>
          <SheetClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-10 top-1/2 size-12 -translate-y-1/2 rounded-full p-[15px] text-zinc-900 dark:text-zinc-100 max-sm:right-4"
              aria-label="Close transaction simulation"
            >
              <X aria-hidden="true" className="size-[18px]" />
            </Button>
          </SheetClose>
        </header>

        {simulation.status === 'success' && (
          <p
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
          >
            Transaction simulation completed successfully.
          </p>
        )}
        {simulation.status === 'reverted' && (
          <p role="alert" aria-atomic="true" className="sr-only">
            Transaction simulation reverted. {simulation.reason}
          </p>
        )}
        {simulation.status === 'provider-failure' && (
          <p role="alert" aria-atomic="true" className="sr-only">
            Transaction simulation failed. {simulation.message}
          </p>
        )}

        {simulation.status === 'loading' && <LoadingContent />}
        {simulation.status === 'success' && (
          <SuccessContent simulation={simulation} networkId={networkId} />
        )}
        {simulation.status === 'reverted' && (
          <RevertedContent reason={simulation.reason} />
        )}
        {simulation.status === 'provider-failure' && (
          <ProviderFailureContent message={simulation.message} />
        )}

        <SimulationFooter
          showResimulate={showResimulate}
          onResimulate={resimulate}
        />
      </SheetContent>
    </Sheet>
  );
}
